// known-secret-value-encoded-variants.ts — phase 04a (spec V2 §4.2, Codex F2).
//
// Mọi dạng một known-value có thể mang khi đi qua sink: raw, base64,
// URL-encode, JSON-escape một tầng (`JSON.stringify(v).slice(1, -1)` — stream
// worker được redact TRƯỚC `JSON.parse`, `attempt-forensics` stringify evidence
// TRƯỚC khi redact) và JSON-escape hai tầng (chuỗi JSON lồng trong chuỗi JSON).
// Probe tái hiện lỗ: reports/evidence/codex-deep-review-261006/
// json-escaped-secret-probe.mjs — `ab"cdefg` trên wire là `ab\"cdefg`, không
// khớp mẫu raw, sống lại sau parse.
//
// Thứ tự: dài trước. Dạng ngắn có thể nằm TRONG dạng dài (`"abcdefgh` là đuôi
// của `\"abcdefgh`); thay dạng ngắn trước sẽ để lại `\[REDACTED]` — escape
// JSON không hợp lệ, làm vỡ `JSON.parse` của stream.

/** Ngưỡng known-value từ `test.env`/stdin (§4.2): ngắn hơn ⇒ không đăng ký (tránh che `true`, `3000`). */
export const KNOWN_SECRET_VALUE_MIN_LENGTH = 8;

/** Lọc giá trị đủ dài để đăng ký known-value (chỉ dùng cho nguồn `test.env`/stdin). */
export function registrableKnownSecretValues(values: Iterable<string>): string[] {
  return [...values].filter((v) => v.length >= KNOWN_SECRET_VALUE_MIN_LENGTH);
}

function jsonEscaped(text: string): string {
  return JSON.stringify(text).slice(1, -1);
}

/** Các literal cần che cho một secret — không trùng, dài trước. Rỗng ⇒ []. */
export function encodedVariantsOfKnownSecret(secret: string): string[] {
  if (!secret) return [];
  const variants = new Set<string>([secret]);
  try {
    const b64 = Buffer.from(secret, "utf8").toString("base64");
    if (b64.length >= 4) variants.add(b64);
  } catch {
    // ignore encoding failure, not fatal to redaction of other variants
  }
  try {
    variants.add(encodeURIComponent(secret));
  } catch {
    // lone surrogate ⇒ URIError; các dạng khác vẫn che
  }
  const once = jsonEscaped(secret);
  variants.add(once);
  variants.add(jsonEscaped(once));
  return [...variants].sort((a, b) => b.length - a.length);
}

/** Literal của mọi secret, dài trước trên TOÀN bộ tập (không chỉ trong từng secret). */
export function encodedVariantsOfKnownSecrets(secretValues: readonly string[]): string[] {
  const all = new Set<string>();
  for (const secret of secretValues) {
    for (const variant of encodedVariantsOfKnownSecret(secret)) all.add(variant);
  }
  return [...all].sort((a, b) => b.length - a.length);
}
